/**
 * Utility functions for URL normalization and query parameter extraction.
 */
export const UrlUtils = {
	/**
	 * Normalizes a URL by removing the protocol prefix and converting it to lowercase.
	 * This function handles various protocol formats including https://, https-iframe://, https-newtab://, etc.
	 *
	 * @param url - The URL string to normalize
	 * @returns The normalized URL string without protocol prefix and in lowercase,
	 *          or an empty string if input is invalid or an error occurs
	 */
	normalizeUrl(url: string): string {
		try {
			if (typeof url !== "string" || url.trim() === "") return "";
			const trimmed = url.trim();
			const [base, query = ""] = trimmed.split("?");

			// Match: https://, https-iframe://, https-newtab://, etc.
			const cleanedBase = base.replace(/^https?(-[a-z]+)?:\/\//i, "").toLowerCase();
			return query ? `${cleanedBase}?${query}` : cleanedBase;
		} catch {
			return "";
		}
	},

	/**
	 * Extracts the value of a specific query parameter from a normalized URL string.
	 * Assumes the input URL has no protocol prefix (e.g., stripped by normalizeUrl).
	 *
	 * @param url - The normalized URL string (e.g., "example.com/path?name=foo")
	 * @param key - The query parameter key to extract
	 * @returns The value of the key, or null if not found or invalid
	 */
	extractQueryParam(url: string, key: string): string | null {
		try {
			if (typeof url !== "string" || url.trim() === "") return null;
			if (typeof key !== "string" || key.trim() === "") return null;

			// Match: ?name=foo, &name=foo
			const queryStart = url.indexOf("?");
			if (queryStart === -1) return null;

			// Extract the query string part of the URL
			// Example: "example.com/path?name=foo&other=bar" -> "?name=foo&other=bar"
			const queryString = url.slice(queryStart);
			const params = new URLSearchParams(queryString);

			// Get the value of the specified key. If the key is not found, return null.
			const value = params.get(key);
			return value && value.trim() !== "" ? value.trim() : null;
		} catch (ex) {
			console.log("[UrlUtils] Error extracting query parameter:\n", ex);
			return null;
		}
	},
};
